import allDrugNames from '../data/drugNames.json';

const RXNAV_BASE = 'https://rxnav.nlm.nih.gov/REST';

/**
 * Filters the local drug name list — no API call, instant results.
 * Shows names that START WITH the query first, then names that contain it
 * as a whole word elsewhere (e.g. "hydrocodone" matches "acetaminophen /
 * hydrocodone" but not "benzhydrocodone" — a different drug that just
 * happens to contain those letters mid-word).
 */
export function getSuggestions(query) {
  const q = query.toLowerCase();
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const wordMatch = new RegExp(`\\b${escaped}`, 'i');

  const startsWith = [];
  const contains = [];

  for (const name of allDrugNames) {
    const lower = name.toLowerCase();
    if (lower.startsWith(q)) {
      startsWith.push(name);
    } else if (wordMatch.test(lower)) {
      contains.push(name);
    }
  }

  return [...startsWith, ...contains].slice(0, 15);
}

/**
 * Display-only: reorders a combo name's " / "-separated ingredients so the
 * one matching what the patient typed appears first — e.g. searching
 * "hydrocodone" shows "hydrocodone / acetaminophen" instead of RxNorm's
 * alphabetical "acetaminophen / hydrocodone", matching how it's more likely
 * written on a real label. Does NOT change the underlying value used for
 * the RxNorm lookup — only what's rendered in the suggestion list.
 */
export function reorderForQuery(name, query) {
  if (!name.includes(' / ')) return name;
  const q = query.toLowerCase();
  const parts = name.split(' / ');
  const idx = parts.findIndex(p => p.toLowerCase().startsWith(q));
  if (idx <= 0) return name;
  return [parts[idx], ...parts.slice(0, idx), ...parts.slice(idx + 1)].join(' / ');
}

/**
 * Fetches available dosage forms for a given RxCUI.
 * Returns SCD (generic) and SBD (brand) concepts — e.g. "Lisinopril 10 MG Oral Tablet".
 * Only called once when the user selects a drug.
 */
export async function getDosages(drugName, rxcui) {
  const res = await fetch(
    `${RXNAV_BASE}/rxcui/${rxcui}/related.json?tty=SCD+SBD`
  );
  if (!res.ok) throw new Error(`RxNav request failed: ${res.status}`);
  const data = await res.json();

  const groups = data?.relatedGroup?.conceptGroup || [];
  const scds = [];
  const sbds = [];

  for (const group of groups) {
    const tty = group.tty;
    for (const p of group.conceptProperties || []) {
      if (tty === 'SCD') scds.push(p);
      else if (tty === 'SBD') sbds.push(p);
    }
  }

  // RxNorm sometimes inserts a qualifier between the drug name and the
  // strength (a salt name, etc. — e.g. "rosuvastatin calcium 20 MG Oral
  // Tablet"). Whether a real pharmacy label keeps or drops that word varies
  // by drug and isn't something we can reliably guess. So when it's
  // present, build BOTH labels — the accurate full name, and a drug-name-
  // only version — and offer both as separate options for the same
  // underlying product, so the patient can match whichever matches their
  // own bottle. Entries with no such qualifier (the common case) just get
  // the one clean label, same as before.
  const escapedName = drugName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const cleanPrefix = new RegExp(`^${escapedName}\\s+(?=\\d)`, 'i');
  const withQualifier = new RegExp(`^(${escapedName})\\s+\\S.*?\\s+(?=\\d)`, 'i');
  // RxNorm puts the BRAND name in a trailing bracket, not as a prefix (e.g.
  // "rosuvastatin calcium 20 MG Oral Tablet [Crestor]"), so a brand search
  // ("Crestor") can't match via the prefix patterns above at all. Detect it
  // separately: if any SBD name ends in "[<drugName>]", treat this as a
  // brand-name search and build labels from strength/form alone — the
  // generic chemical name isn't what's on a brand patient's bottle either.
  const bracketSuffix = new RegExp(`\\[${escapedName}\\]\\s*$`, 'i');

  function labelsFor(rawName) {
    const name = rawName.replace(/\s*\[.*?\]/g, '').trim();
    if (cleanPrefix.test(name)) return [name.replace(cleanPrefix, '').trim()];
    if (withQualifier.test(name)) return [name, name.replace(withQualifier, '$1 ').trim()];
    return [name];
  }

  // Build a map keyed by strength+form so each unique label appears once,
  // but all brand RxCUIs are stored alongside
  const strengthMap = new Map();

  function addLabel(label, entryRxcui) {
    if (!strengthMap.has(label)) {
      strengthMap.set(label, { label, rxcui: entryRxcui, brandRxcuis: [] });
    }
  }

  if (sbds.some(p => bracketSuffix.test(p.name))) {
    for (const p of sbds) {
      if (!bracketSuffix.test(p.name)) continue;
      const withoutBracket = p.name.replace(/\s*\[.*?\]\s*$/, '').trim();
      const strengthOnly = withoutBracket.replace(/^\S+(?:\s+\S+)*?\s+(?=\d)/, '').trim();
      addLabel(strengthOnly, p.rxcui);
      addLabel(withoutBracket, p.rxcui);
    }
  } else {
    // Use SCD entries; fall back to SBD if no generics exist for this drug
    const source = scds.length > 0 ? scds : sbds;
    for (const p of source) {
      for (const label of labelsFor(p.name)) addLabel(label, p.rxcui);
    }
    // Attach all SBD RxCUIs to the matching strength entry/entries
    for (const p of sbds) {
      for (const label of labelsFor(p.name)) {
        if (strengthMap.has(label)) {
          strengthMap.get(label).brandRxcuis.push(p.rxcui);
        }
      }
    }
  }

  return [...strengthMap.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Looks up the RxCUI for a drug name.
 * The display name always comes from the local list — this only fetches the ID.
 */
export async function getRxCUI(name) {
  const res = await fetch(
    `${RXNAV_BASE}/rxcui.json?name=${encodeURIComponent(name)}&search=1`
  );
  if (!res.ok) throw new Error(`RxNav request failed: ${res.status}`);
  const data = await res.json();
  const ids = data?.idGroup?.rxnormId || [];
  return ids[0] ?? null;
}
